import { getRoomTagEventCategory } from '../../../../../../modules/room-tag-storage';

export const EXPERIMENT_ID = 'tm-8174-facilities-in-mosaic-v1';

const VERSION_RELEVANT = 1;
const MAX_FEATURES = 6;

const DETAILS_PANEL = '.mosaicPopup__content--details';
const CONTROL_VARIANT = '.mosaicPopup [data-mosaic-features-variant="control"]';
const RELEVANT_VARIANT = '.mosaicPopup [data-mosaic-features-variant="relevant"]';

// Primary features first; the fallbacks are the facility filters Clients in that category use most.
const FEATURE_SETS = {
  meeting: {
    primary: ['wifi', 'conference-calls', 'projector', 'ac', 'flipchart', 'parking'],
    fallbacks: ['tv', 'whiteboard', 'natural-light', 'pa-system', 'quiet-space', 'storage-space'],
  },
  business: {
    primary: ['wifi', 'pa-system', 'projector', 'ac', 'flipchart', 'parking'],
    fallbacks: ['tv', 'own-music-allowed', 'own-dj-allowed', 'natural-light', 'conference-calls', 'whiteboard'],
  },
  wedding: {
    primary: ['own-music-allowed', 'wedding-licence', 'pa-system', 'parking', 'own-dj-allowed', 'ac'],
    fallbacks: ['natural-light', 'wifi', 'projector', 'storage-space'],
  },
  party: {
    primary: ['own-music-allowed', 'pa-system', 'promoted-ticketed-events', 'minimum-spend-pricing', 'own-dj-allowed', 'ac'],
    fallbacks: ['projector', 'wifi', 'tv', 'wedding-licence', 'natural-light'],
  },
  production: {
    primary: ['wifi', 'natural-light', 'pa-system', 'storage-space', 'cargo-lift', 'ac'],
    fallbacks: ['quiet-space', 'projector', 'own-music-allowed', 'tv', 'flipchart'],
  },
};

// Venue-type tags and visitors without an arrival tag, e.g. straight from a search engine.
const DEFAULT_FEATURE_SET = {
  primary: ['own-music-allowed', 'pa-system', 'own-dj-allowed', 'projector', 'wifi', 'tv'],
  fallbacks: ['ac', 'wedding-licence', 'quiet-space', 'natural-light', 'whiteboard', 'conference-calls'],
};

let version = null;
let detailsPanelSeen = false;
let impressionSent = false;

/**
 * @param {{id: string, label: string}[]} candidates the features the room offers
 * @param {?string} eventCategory the arrival tag's event category
 * @returns {{id: string, label: string}[]} up to six features, primary ones first
 */
export const selectRelevantFeatures = (candidates, eventCategory) => {
  const featureSet = FEATURE_SETS[eventCategory] ?? DEFAULT_FEATURE_SET;
  const candidatesById = new Map(candidates.map((candidate) => [candidate.id, candidate]));

  return [...featureSet.primary, ...featureSet.fallbacks]
    .filter((id) => candidatesById.has(id))
    .slice(0, MAX_FEATURES)
    .map((id) => candidatesById.get(id));
};

const dropVariant = (selector) => {
  document.querySelectorAll(selector).forEach((el) => el.remove());
};

const renderFeatures = (listEl, features) => {
  features.forEach((feature, index) => {
    const item = document.createElement('p');
    item.className = 'text-sm font-normal text-grey-700 text-nowrap mr-1 flex items-center';

    if (index !== 0) {
      const separator = document.createElement('span');
      separator.className = 'text-grey-800 mr-1 text-xxxs font-thin';
      separator.textContent = '•';
      item.append(separator, ' ');
    }

    item.append(feature.label);
    listEl.appendChild(item);
  });
};

// The side panel is hidden by CSS below the desktop breakpoint; its computed display tells whether the
// visitor can see it regardless of whether the modal itself is shown yet.
const isDetailsPanelVisible = () => {
  const panel = document.querySelector(DETAILS_PANEL);

  return panel !== null && window.getComputedStyle(panel).display !== 'none';
};

const sendImpressionIfDue = () => {
  if (impressionSent || !detailsPanelSeen || version === null) {
    return;
  }

  impressionSent = true;

  App.track.event('experiment_impression', {
    experiment_id: EXPERIMENT_ID,
    variant_id: version,
  });
};

/**
 * Keeps only the features block the visitor is meant to see. Both are server-rendered because room pages
 * are edge-cached - one HTML response reaches everyone, so the split can only happen in the browser.
 * Runs on the live mosaic, after it has been cloned into the page.
 *
 * Rooms with nothing relevant to show for the visitor's arrival category keep the current list and stay
 * out of the experiment, since both versions would look the same to them.
 *
 * @todo TM-8174 keep the winning variant and drop the other one once the experiment is settled
 * @returns {Promise<?number>} the allocated version, or null for visitors outside the experiment
 */
export const applyMosaicFeaturesExperiment = async () => {
  const relevantBlock = document.querySelector(`${RELEVANT_VARIANT}[data-features]`);
  if (!relevantBlock) {
    return null;
  }

  const candidates = JSON.parse(relevantBlock.dataset.features);
  const eventCategory = candidates.length > 0 ? await getRoomTagEventCategory() : null;
  const features = selectRelevantFeatures(candidates, eventCategory);

  if (features.length === 0) {
    dropVariant(RELEVANT_VARIANT);

    return null;
  }

  version = window.getVersionFromCookieId?.(EXPERIMENT_ID) ?? null;

  if (version !== VERSION_RELEVANT) {
    dropVariant(RELEVANT_VARIANT);
    sendImpressionIfDue();

    return version;
  }

  renderFeatures(relevantBlock.querySelector('.js-mosaic-relevant-features'), features);
  dropVariant(CONTROL_VARIANT);
  document.querySelectorAll(RELEVANT_VARIANT).forEach((el) => el.classList.remove('hidden'));
  sendImpressionIfDue();

  return version;
};

/**
 * Sent when the gallery opens with the side panel showing, because that is the first moment the two
 * versions differ - visitors who never open it, or open it on a viewport too narrow for the panel, would
 * only add noise to the results. An open before the version is known is reported as soon as it is.
 */
export const trackMosaicFeaturesImpression = () => {
  if (!isDetailsPanelVisible()) {
    return;
  }

  detailsPanelSeen = true;
  sendImpressionIfDue();
};
